export type FormName =
	| "validation"
	| "sign-in"
	| "sign-in-cross"
	| "sign-up"
	| "sign-up-ncad"
	| "sign-up-ncad-completed"
	| "sign-up-partner"
	| "forgotPassword"
	| "reset"
	| "update-contact"
	| "update-password"
	| "validate-conflicting-email"
	| "sign-in-unify-password"
	| "unlock-protected-feature"
	| "simple-sign-in"
	| "public-email-confirmation"
	| "private-mfa-positive-data";

export enum FormNameEnum {
	VALIDATION = "validation",
	SIGN_IN = "sign-in",
	SIGN_IN_CROSS = "sign-in-cross",
	SIGN_UP = "sign-up",
	SIGN_UP_NCAD = "sign-up-ncad",
	SIGN_UP_NCAD_COMPLETED = "sign-up-ncad-completed",
	SIGN_UP_PARTNER = "sign-up-partner",
	FORGOT_PASSWORD = "forgotPassword",
	RECOVERY_PASSWORD = "recovery-password",
	RESET = "reset",
	UPDATE_CONTACT = "update-contact",
	UPDATE_PASSWORD = "update-password",
	VALIDATE_CONFLICTING_EMAIL = "validate-conflicting-email",
	SIGN_IN_UNIFY_PASSWORD = "sign-in-unify-password",
	UNLOCK_PROTECTED_FEATURE = "unlock-protected-feature",
	SIMPLE_SIGN_IN = "simple-sign-in",
	PUBLIC_EMAIL_CONFIRMATION = "public-email-confirmation",
	PRIVATE_MFA_POSITIVE_DATA = "private-mfa-positive-data",
}

export type FormContext = {
	formBrand: string;
	formId?: string;
	formName: FormName;
	formStep?: string;
	formType?: string;
	formVersion: string;
};

export type StepContext = {
	stepId: string;
	stepName: string;
	stepNumber: number;
};

export type DefaultFormContext = {
	formContext: FormContext;
	stepContext?: StepContext;
};

export type DefaultFormEventPayload = {
	[key in FormName]: DefaultFormContext;
};

export type ElementEventPayload = {
	elementType?: ElementEventType;
	htmlId?: string;
	location?: string;
	name?: string;
	position?: string;
	text?: string;
	data?: object;
};

export type UserAuthenticatedProps = {
	authenticationType:
		| "cached-sign-in"
		| "sign-up"
		| "sign-up-ncad"
		| "sign-up-ncad-completed"
		| "sign-up-partner-completed"
		| "sign-in"
		| "pin"
		| "email-sign-in"
		| "sign-in-validate-identity"
		| "public-email-confirmation"
		| "atende-sign-in";
	customerId: string;
	isUnified?: boolean;
};

export type DispatchedEvent = {
	[key: string]: boolean;
};

export type FieldProps = {
	fieldName: string;
	fieldId?: string;
	fieldLabel?: string;
	fieldValue?: string | boolean | number;
	autofilled?: boolean;
};

export type FieldEventPayload = {
	field: FieldProps;
	isInputtedEvent?: boolean;
	ignoreAlreadyDispatch?: boolean;
	stepContext?: StepContext;
};

export type ErrorResponseProps = {
	error_slug: string;
	error_code: string;
	message: string;
};

export type FormStepProps = {
	stepName: string;
	stepPosition: number;
};

export type FormContinuedStepSlug =
	| "termsOfUse"
	| "full_name"
	| "birthdate"
	| "email"
	| "token"
	| "phone"
	| "password"
	| "kba"
	| "choose_method";

export type FormContinuedStep<T> = {
	slug: T;
	label: string;
	step: number;
};

export type FormContinuedStepType =
	| FormContinuedStep<FormContinuedStepSlug>
	| FormContinuedStep<SignUpStepSlug>;

export type SignUpStepSlug =
	| "termsOfUse"
	| "full_name"
	| "birthdate"
	| "email"
	| "token"
	| "phone"
	| "sms"
	| "password"
	| "kba";

export type AppInfoTrackedPayload = {
	storeAppVersion: string;
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	deviceInfo: any;
};

export type ElementEventType =
	| "button"
	| "button/text"
	| "button/icon"
	| "button/image"
	| "link"
	| "link/text"
	| "link/icon"
	| "link/image"
	| "card"
	| "card/button"
	| "card/text"
	| "card/icon"
	| "card/image"
	| "tab"
	| "content"
	| "accordion"
	| "adsense"
	| "close"
	| "collapse"
	| "modal"
	| "banner";

export type ProjectType = "ac" | "cp";

export type UseTrackingProps = {
	formName: FormName;
};

export type FormSubmittedEventProps = {
	// biome-ignore lint/suspicious/noExplicitAny: <explanation>
	formPayload: any;
	stepContext?: StepContext;
};

export type FormContextProps = {
	formBrand?: string;
	formId: string;
	formName?: string;
	formType: string;
	formVersion?: string;
};

export interface FormContinuedEventProps extends FormSubmittedEventProps {
	formContext?: FormContextProps;
}

export interface FormStartedEventProps
	extends Pick<FormContinuedEventProps, "stepContext"> {}

export interface FormErroredProps {
	errorResponse: ErrorResponseProps;
	stepContext?: StepContext;
}

export type CheckRegisterStatusEventProps = {
	isRegistered?: string;
	customerId?: string;
};

export type UserAttributeUpdatedEventProps = {
	devedorId: number;
	customerId: string;
	customerEmail: string;
	key: string;
	newValue: string;
	oldValue: string;
};
